/**
 * Sign in with OpenMarkets — the ONE shared auth instance for the whole app.
 *
 * Why a singleton and not the SDK's React hook: the hook creates its own internal instance
 * and does NOT expose `getApiKey()`/`refresh()`. We must use `getApiKey()` (access keys expire
 * in 7 days; it silently renews from a 90-day refresh token) and we must NEVER let two
 * instances refresh the same session — refresh tokens rotate, and presenting a retired one is
 * treated as theft and revokes the entire install. So there is exactly one instance, created
 * lazily in the browser, and every key read goes through its single-in-flight `getApiKey()`.
 *
 * greenbook also supports a legacy manual pasted key; {@link resolveApiKey} prefers the SDK
 * session and falls back to that. Feature gating reads the granted scopes. No credential at
 * all = signed-out PUBLIC mode (lib/client.ts rewrites onto /api/public), not a wall.
 */
import { useSyncExternalStore } from "react";
import {
  createOpenMarketsAuth,
  entitlementErrorFromBody,
  hasCallbackParams,
  type OpenMarketsSession,
  type SignInOptions,
  type SignInResult,
  type UpgradeResult,
} from "@openmarketsai/app-sdk";
import { clearToken, getToken, tokenStore } from "./auth.js";
import { setTradingMode } from "./trading.js";

/**
 * Scope codes greenbook gates features on.
 *
 * REQUIRED vs OPTIONAL is NOT set here — the SDK takes no scopes in code (0.6:
 * `createOpenMarketsAuth({ clientId, redirectUri, apiBase, consoleBase, storage, mode })`); the
 * request is configured on the `greenbook` app registration in the OpenMarkets publisher
 * console. Configure it as:
 *
 *   required  reference:read, markets:read   — the only things a Free plan is assumed to
 *                                               include (the public docs don't list what Free
 *                                               includes; see FRICTION.md), so sign-in never
 *                                               blocks a Free user
 *   optional  everything below               — each feature degrades gracefully without it
 *
 * Signed-out visitors need none of this: Markets + Contest detail read the org-keyed public
 * feed (functions/api/public). Every optional scope is checked against the GRANTED session
 * scopes (gate on what you received, not what you asked for) and every 403
 * `entitlement_required` / `scope_not_granted` becomes an Unlock prompt (see `upgradeFor`).
 */
export const SCOPES = {
  trade: "trade:execute",
  depth: "depth:read",
  pricing: "pricing:history",
  gamestate: "gamestate:read",
  stream: "stream:realtime",
  account: "account:read",
  // Gated by the API's answer, not pre-gated on the session: whether the greenbook
  // registration requests these isn't knowable from code, and pre-gating on a scope the
  // registration never asks for would lock the page forever. A 403 here → Unlock prompt.
  signals: "signals:read",
  arbitrage: "arbitrage:read",
} as const;

let instance: ReturnType<typeof createOpenMarketsAuth> | null = null;
/** Lazily create the single instance (browser only — uses window/crypto/storage). */
function auth(): ReturnType<typeof createOpenMarketsAuth> {
  if (!instance) {
    instance = createOpenMarketsAuth({
      clientId: "greenbook",
      // One build serves both envs; the trailing slash matters (exact-match redirect URI).
      redirectUri: window.location.origin + "/",
      apiBase: import.meta.env.VITE_OPENMARKETS_BASE || undefined,
      consoleBase: import.meta.env.VITE_OPENMARKETS_CONSOLE || undefined,
    });
  }
  return instance;
}

/**
 * The ONE app-sdk instance, for other OpenMarkets SDKs that take it as their credential (the
 * order SDK's `{ auth }` option — see lib/orderClient.ts). Hand out this instance; never
 * construct a second `createOpenMarketsAuth`: two instances refreshing the same session
 * present a rotated refresh token and revoke the whole install.
 */
export function getOmAuth(): ReturnType<typeof createOpenMarketsAuth> {
  return auth();
}

/** True when the credential is an SDK session (not the legacy manual key, not signed out). */
export function hasSdkSession(): boolean {
  ensureInit();
  return !!session;
}

/* ------------------------------------------------------------------ session store */

let session: OpenMarketsSession | null = null;
let inited = false;
const sessionListeners = new Set<() => void>();

function ensureInit(): void {
  if (inited || typeof window === "undefined") return;
  inited = true;
  try {
    session = auth().getSession();
  } catch {
    session = null;
  }
}
if (typeof window !== "undefined") ensureInit();

function setSession(s: OpenMarketsSession | null): void {
  session = s;
  // Hard safety: a session without trade:execute can't place real orders — force the global
  // trading mode back to paper so nothing is stuck "armed" for real.
  if (s && !s.scopes.includes(SCOPES.trade)) {
    try {
      setTradingMode("paper");
    } catch {
      /* ignore */
    }
  }
  for (const l of sessionListeners) l();
}

/** Re-read the SDK session from storage (used by the opener after a popup completes). */
export function syncSessionFromStorage(): void {
  ensureInit();
  try {
    setSession(auth().getSession());
  } catch {
    /* ignore */
  }
}

export function getOmSession(): OpenMarketsSession | null {
  ensureInit();
  return session;
}

export function useOmSession(): OpenMarketsSession | null {
  return useSyncExternalStore(
    (cb) => {
      sessionListeners.add(cb);
      return () => sessionListeners.delete(cb);
    },
    () => session,
    () => null,
  );
}

/* ------------------------------------------------------- status (callback in-flight) */

let status: { loading: boolean; error: string | null } = {
  loading: typeof window !== "undefined" && safeHasCallback(),
  error: null,
};
const statusListeners = new Set<() => void>();
function safeHasCallback(): boolean {
  try {
    return hasCallbackParams();
  } catch {
    return false;
  }
}
function setStatus(next: Partial<typeof status>): void {
  status = { ...status, ...next };
  for (const l of statusListeners) l();
}
export function setAuthLoading(loading: boolean): void {
  setStatus({ loading });
}
export function setAuthError(error: string | null): void {
  setStatus({ error });
}
export function useAuthStatus(): { loading: boolean; error: string | null } {
  return useSyncExternalStore(
    (cb) => {
      statusListeners.add(cb);
      return () => statusListeners.delete(cb);
    },
    () => status,
    () => ({ loading: false, error: null }),
  );
}

/* -------------------------------------------------------------- key resolution */

/**
 * The API key for the NEXT request. SDK session → `getApiKey()` (single-in-flight, refreshes
 * silently); a dead refresh token → clear the session and return null (caller boots to the
 * gate). No session → the manual pasted key, if any. NEVER caches a key — the whole point of
 * `getApiKey()` is that we ask it every time so a rotated/renewed key is always current.
 */
export async function resolveApiKey(): Promise<string | null> {
  ensureInit();
  if (session) {
    // getApiKey() single-flights internally (concurrent callers share one refresh) and returns
    // null only when the session is truly dead (refresh token expired/revoked/reused) → boot to
    // the gate. A transient network failure THROWS instead, which propagates as a failed request
    // and must NOT sign the user out.
    const key = await auth().getApiKey();
    if (!key) {
      killSession();
      return null;
    }
    return key;
  }
  return getToken(); // manual fallback (or null)
}

/* --------------------------------------------------------------- lifecycle */

/**
 * Open the consent flow (popup by default; the SDK owns all window coordination and falls
 * back to a redirect if the popup is blocked). RESOLVES with the outcome — nothing throws, so
 * `denied` (cancel / closed window) is a normal result, never an error. On success we store
 * the session; the caller just branches on `status` for UI.
 */
export async function signIn(opts?: SignInOptions): Promise<SignInResult> {
  const r = await auth().signIn(opts);
  if (r.status === "success") setSession(r.session);
  return r;
}

/**
 * Complete the flow on page load. Safe to call unconditionally — a load with no sign-in
 * response returns `{ status: 'none' }`. Never throws. When it runs inside the sign-in popup
 * the SDK reports to the opener and self-closes. On success we store the session.
 */
export async function handleCallback(): Promise<SignInResult> {
  const r = await auth().handleCallback();
  if (r.status === "success") setSession(r.session);
  return r;
}

/** Local sign-out of the SDK session (does NOT revoke — only the user can, from their dash). */
function killSession(): void {
  try {
    auth().signOut();
  } catch {
    /* ignore */
  }
  setSession(null);
}

/* --------------------------------------------------------------- upgrades */

/**
 * Send the user to unlock what their plan or grant lacks, and bring them back (SDK 0.6
 * `auth.upgrade()`; OpenMarkets charges the user, greenbook handles no billing). `target` is
 * either the API's 403 body pieces (`code` + `details`, which carry the exact upgrade page) or
 * capability codes. Must be called from a click (it opens a popup).
 *
 * Then, per the docs: `done` → re-consent (`signIn({ prompt: 'consent' })`) so the new
 * capability lands in this install's grant. A `scope_not_granted` refusal, or `no_upgrade`
 * (no plan sells it — so the plan already has it and only the grant is short), skips the
 * upgrade page and goes straight to re-consent.
 */
export async function upgradeFor(
  target: { code: string; message?: string; details?: Record<string, unknown> } | string[],
): Promise<{ upgrade: UpgradeResult | null; consent: SignInResult | null }> {
  let upgrade: UpgradeResult | null = null;
  if (Array.isArray(target)) {
    upgrade = await auth().upgrade(target);
  } else if (target.code !== "scope_not_granted") {
    const err = entitlementErrorFromBody(403, {
      error: { code: target.code, message: target.message ?? "", details: target.details ?? {} },
    });
    upgrade = err && err.upgradeUrl ? await auth().upgrade(err) : null;
  }
  const reconsent =
    upgrade === null ||
    upgrade.status === "done" ||
    (upgrade.status === "error" && upgrade.code === "no_upgrade");
  if (!reconsent) return { upgrade, consent: null };
  const consent = await signIn({ prompt: "consent" });
  return { upgrade, consent };
}

/**
 * After a REDIRECT-mode upgrade (popup blocked) the page reloads with `?om_upgrade=done|canceled`.
 * Read + strip it once on load. A `done` still needs a re-consent, which must come from a
 * click (popup) — the App shows a banner for it (see UpgradeReturnBanner).
 */
let upgradeReturn: "done" | "canceled" | null = null;
const upgradeListeners = new Set<() => void>();
export function consumeUpgradeReturn(): void {
  try {
    upgradeReturn = auth().handleUpgradeReturn();
  } catch {
    upgradeReturn = null;
  }
  for (const l of upgradeListeners) l();
}
export function clearUpgradeReturn(): void {
  upgradeReturn = null;
  for (const l of upgradeListeners) l();
}
export function useUpgradeReturn(): "done" | "canceled" | null {
  return useSyncExternalStore(
    (cb) => {
      upgradeListeners.add(cb);
      return () => upgradeListeners.delete(cb);
    },
    () => upgradeReturn,
    () => null,
  );
}

/** Full sign-out: SDK session AND any manual key → back to public (signed-out) mode. */
export function disconnect(): void {
  killSession();
  clearToken();
}

/**
 * A 401 from the API (revoked from the dashboard, uninstalled, dead key) or a refresh failure
 * means the credential is gone. Clear everything and drop back to public mode — never loop.
 * Only ever called for a request that carried a credential (api.ts `reactToError`).
 */
export function onAuthRejected(): void {
  disconnect();
}

/* --------------------------------------------------------------- gating / identity */

export function can(scope: string): boolean {
  ensureInit();
  return session?.scopes.includes(scope) ?? false;
}

/**
 * Non-React scope check that DEFERS for the manual-key path: an SDK session honors its granted
 * scopes; a manual pasted key (no session) returns true so we don't over-block (the server is
 * the authority there). Used by the stream layer to avoid opening a doomed WS.
 */
export function hasScopeOrManual(scope: string): boolean {
  ensureInit();
  if (session) return session.scopes.includes(scope);
  return !!getToken();
}

export function isAuthed(): boolean {
  ensureInit();
  return !!session || !!getToken();
}

/** Non-React: may this credential place real orders? SDK honors the trade scope; a manual key
 * defers (the server is the authority). Used by the order client (orderClient.ts) + the trading-mode switch. */
export function canTrade(): boolean {
  ensureInit();
  if (session) return session.scopes.includes(SCOPES.trade);
  return !!getToken();
}

/** Stable id for the current credential (session install, or "manual"); null when signed out. */
export function authIdentity(): string | null {
  ensureInit();
  if (session) return `sdk:${session.installId}`;
  return getToken() ? "manual" : null;
}

/** Fires whenever auth changes — SDK session OR manual key. */
export function subscribeAuth(cb: () => void): () => void {
  sessionListeners.add(cb);
  const unToken = tokenStore.subscribe(cb);
  return () => {
    sessionListeners.delete(cb);
    unToken();
  };
}

function useAuthTick(): void {
  // Subscribe a component to BOTH auth sources so identity/scope reads re-render on change.
  useOmSession();
  useSyncExternalStore(tokenStore.subscribe, tokenStore.getSnapshot, () => null);
}

export function useIsAuthed(): boolean {
  useAuthTick();
  return isAuthed();
}

export function useAuthIdentity(): string | null {
  useAuthTick();
  return authIdentity();
}

export function useCanScope(scope: string): boolean {
  const s = useOmSession();
  const manual = useSyncExternalStore(tokenStore.subscribe, tokenStore.getSnapshot, () => null);
  if (s) return s.scopes.includes(scope);
  if (manual) return true; // manual key → don't over-block; defer to can_execute + server
  return false;
}

/**
 * Can this visitor use a feature? `signed_out` → show "Sign in"; `scope` → signed in, but the
 * granted session lacks `scope` → show "Unlock". No `scope` = any signed-in credential.
 */
export type Access = { ok: true } | { ok: false; reason: "signed_out" } | { ok: false; reason: "scope"; scope: string };

export function useAccess(scope?: string): Access {
  const authed = useIsAuthed();
  const has = useCanScope(scope ?? "");
  if (!authed) return { ok: false, reason: "signed_out" };
  if (scope && !has) return { ok: false, reason: "scope", scope };
  return { ok: true };
}

/** Execution features (order entry). SDK session honors `trade:execute`; manual key defers. */
export function useCanTrade(): boolean {
  return useCanScope(SCOPES.trade);
}
